import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, extractErrorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useBrand } from "../lib/brand";
import Sidebar from "../components/Sidebar";
import PageTopBar from "../components/PageTopBar";
import { useConfirm } from "../components/ConfirmDialog";
import { toast } from "sonner";
import {
    UserPlus,
    Trash2,
    Save,
    X,
    Shield,
    ShieldCheck,
    User as UserIcon,
    RefreshCw,
    Users as UsersIcon,
    LogIn,
    Check,
    UserX,
} from "lucide-react";

const ROLE_OPTIONS = [
    { value: "user", label: "Usuario" },
    { value: "admin", label: "Admin" },
    { value: "super_admin", label: "Super Admin" },
];

const ROLE_STYLES = {
    super_admin: "border-periwinkle-500/50 text-periwinkle-800",
    admin: "border-emerald-400/40 text-emerald-300",
    user: "border-ink-600 text-fog-400",
};

const ROLE_ICONS = {
    super_admin: ShieldCheck,
    admin: Shield,
    user: UserIcon,
};

export default function Users() {
    const { user: me, startImpersonation } = useAuth();
    const { brands } = useBrand();
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [draft, setDraft] = useState(blankDraft());
    const confirm = useConfirm();

    const isSuper = me?.role === "super_admin";

    // Bulk-deactivate selection. Only super_admins can multi-select +
    // act on many users at once; admins keep the row-by-row workflow
    // they already have. The set holds user IDs that the operator has
    // ticked, and gets cleared on every successful action or list
    // reload so the UI never lies about what's "selected".
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const toggleSelected = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const clearSelection = () => setSelectedIds(new Set());

    // Status filter chips. The list returned by /api/users excludes
    // soft-deleted users by definition, so we only need to switch
    // between Active / Inactive / All. Default to "active" because
    // that's the actionable subset operators want 99% of the time.
    const [statusFilter, setStatusFilter] = useState("active");
    const filteredUsers = useMemo(() => {
        if (statusFilter === "active") {
            return users.filter((u) => u.is_active !== false);
        }
        if (statusFilter === "inactive") {
            return users.filter((u) => u.is_active === false);
        }
        return users;
    }, [users, statusFilter]);
    const activeCount = useMemo(
        () => users.filter((u) => u.is_active !== false).length,
        [users],
    );
    const inactiveCount = useMemo(
        () => users.filter((u) => u.is_active === false).length,
        [users],
    );

    // Users eligible for bulk action: everyone EXCEPT super_admins
    // (the backend rejects deactivating peers) and self. Selecting
    // them in the UI would only generate "skipped" entries in the
    // response, so we hide the checkbox entirely. Limited to the
    // CURRENT filter (active vs inactive vs all) so "select all" only
    // grabs what the operator is actually looking at.
    const selectableIds = useMemo(() => {
        return filteredUsers
            .filter(
                (u) => u.role !== "super_admin" && u.id !== me?.id,
            )
            .map((u) => u.id);
    }, [filteredUsers, me?.id]);
    const allSelected =
        selectableIds.length > 0 &&
        selectableIds.every((id) => selectedIds.has(id));
    const toggleSelectAll = () => {
        if (allSelected) {
            clearSelection();
        } else {
            setSelectedIds(new Set(selectableIds));
        }
    };

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/users");
            setUsers(r.data || []);
        } catch (e) {
            toast.error(extractErrorMessage(e, "Error cargando usuarios"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    // Drop any selected IDs that disappeared from the latest list
    // (e.g. after a bulk action) so the toolbar count stays accurate.
    useEffect(() => {
        if (selectedIds.size === 0) return;
        const present = new Set(users.map((u) => u.id));
        let changed = false;
        const next = new Set();
        for (const id of selectedIds) {
            if (present.has(id)) {
                next.add(id);
            } else {
                changed = true;
            }
        }
        if (changed) setSelectedIds(next);
    }, [users, selectedIds]);

    // Status filter chips. See definitions above for activeCount/inactiveCount.

    const bulkDeactivate = async () => {
        if (selectedIds.size === 0) return;
        const ids = Array.from(selectedIds);
        const ok = await confirm({
            title: `¿Desactivar ${ids.length} usuario${ids.length === 1 ? "" : "s"}?`,
            description:
                "No podrán entrar hasta que los reactives manualmente. Su data se conserva (chats, librerías, ratings).",
            confirmLabel: `Sí, desactivar (${ids.length})`,
            cancelLabel: "Cancelar",
            tone: "destructive",
            kind: "delete",
        });
        if (!ok) return;
        try {
            const r = await api.post("/users/bulk-deactivate", { ids });
            const { deactivated_count, skipped_count, skipped } = r.data || {};
            if (deactivated_count > 0) {
                toast.success(
                    `Desactivados ${deactivated_count} usuario${deactivated_count === 1 ? "" : "s"}`,
                );
            }
            if (skipped_count > 0) {
                // Show a concise warning rather than the full list — the
                // operator usually only cares that SOMETHING was skipped
                // (self, peer super_admin, not found). The full detail
                // lands in the network tab for debugging.
                toast.warning(
                    `${skipped_count} se omitieron · ${(skipped || [])
                        .slice(0, 3)
                        .map((s) => s.reason)
                        .join(", ")}`,
                );
            }
            clearSelection();
            load();
        } catch (e) {
            toast.error(extractErrorMessage(e, "Error en desactivación masiva"));
        }
    };

    const bulkDelete = async () => {
        if (selectedIds.size === 0) return;
        const ids = Array.from(selectedIds);
        const ok = await confirm({
            title: `¿Eliminar ${ids.length} usuario${ids.length === 1 ? "" : "s"}?`,
            // Deliberately stronger wording than the deactivate dialog:
            // delete = permanently hidden across the whole platform, not
            // a temporary disable. Mention the audit-data safety net so
            // the operator knows historical chats stay attributed
            // (just without a visible owner name).
            description:
                "Los usuarios quedarán ocultos en TODA la plataforma (listas, filtros, reportes). Su data histórica (chats, librerías) se conserva pero sin nombre visible. Esta acción es difícil de revertir manualmente.",
            confirmLabel: `Sí, eliminar (${ids.length})`,
            cancelLabel: "Cancelar",
            tone: "destructive",
            kind: "delete",
        });
        if (!ok) return;
        try {
            const r = await api.post("/users/bulk-delete", { ids });
            const { deleted_count, skipped_count, skipped } = r.data || {};
            if (deleted_count > 0) {
                toast.success(
                    `Eliminados ${deleted_count} usuario${deleted_count === 1 ? "" : "s"}`,
                );
            }
            if (skipped_count > 0) {
                toast.warning(
                    `${skipped_count} se omitieron · ${(skipped || [])
                        .slice(0, 3)
                        .map((s) => s.reason)
                        .join(", ")}`,
                );
            }
            clearSelection();
            load();
        } catch (e) {
            toast.error(extractErrorMessage(e, "Error en eliminación masiva"));
        }
    };

    const softDelete = async (u) => {
        const ok = await confirm({
            title: `¿Eliminar a "${u.name || u.email}"?`,
            description:
                "Este usuario quedará oculto en TODA la plataforma: listas, búsquedas, filtros y reportes. Sus datos históricos (chats, librerías) se conservan pero sin nombre visible. Esta acción es difícil de revertir manualmente.",
            confirmLabel: "Sí, eliminar",
            cancelLabel: "Cancelar",
            tone: "destructive",
            kind: "delete",
        });
        if (!ok) return;
        try {
            await api.post(`/users/${u.id}/soft-delete`);
            toast.success(`Usuario "${u.name || u.email}" eliminado`);
            load();
        } catch (e) {
            toast.error(extractErrorMessage(e, "No se pudo eliminar"));
        }
    };

    const impersonate = async (u) => {
        const ok = await confirm({
            title: `Entrar como "${u.name || u.email}"`,
            description:
                "Verás la plataforma exactamente como la ve este usuario. Podrás volver a tu sesión desde el banner superior en cualquier momento.",
            confirmLabel: "Sí, entrar",
            cancelLabel: "Cancelar",
            tone: "primary",
            kind: "login",
        });
        if (!ok) return;
        try {
            const r = await api.post(`/users/${u.id}/impersonate`);
            startImpersonation(r.data.token, r.data.user, r.data.impersonated_by);
            // SPA navigation — see auth.jsx for rationale on why we
            // dropped the hard reload here (avoids the 17-request burst
            // that was triggering Cloudflare 520 cascades in production).
            navigate("/");
        } catch (e) {
            toast.error(extractErrorMessage(e, "No se pudo impersonar"));
        }
    };

    const activeCreatedCount = useMemo(() => {
        if (!me) return 0;
        return users.filter(
            (u) => u.created_by === me.id && u.is_active !== false,
        ).length;
    }, [users, me]);

    const myQuota = me?.max_users_quota || 0;

    const startCreate = () => {
        setCreating(true);
        setEditingId(null);
        setDraft(blankDraft(isSuper ? "user" : "user"));
    };

    const startEdit = (u) => {
        setEditingId(u.id);
        setCreating(false);
        setDraft({
            email: u.email,
            name: u.name || "",
            role: u.role || "user",
            brand_access: u.brand_access || [],
            max_users_quota: u.max_users_quota ?? 5,
            monthly_image_quota: u.monthly_image_quota ?? 0,
            is_active: u.is_active !== false,
        });
    };

    const cancel = () => {
        setCreating(false);
        setEditingId(null);
        setDraft(blankDraft());
    };

    const submit = async () => {
        const quotaNum = Number(draft.monthly_image_quota) || 0;
        if (quotaNum < 0 || quotaNum > 100_000) {
            toast.error("La cuota mensual debe estar entre 0 y 100.000");
            return;
        }
        const payload = {
            name: draft.name.trim(),
            role: draft.role,
            brand_access: draft.brand_access,
            monthly_image_quota: quotaNum,
        };
        if (isSuper) {
            payload.max_users_quota = Number(draft.max_users_quota) || 0;
        }
        const isEdit = Boolean(editingId);
        try {
            if (isEdit) {
                payload.is_active = draft.is_active;
                // Only send `email` when a super_admin actually changed
                // it — the backend rejects the field for admins and
                // ignores no-op changes but sending it unconditionally
                // would trip the 403 for regular admins.
                if (isSuper) {
                    const original = users.find((u) => u.id === editingId);
                    const newMail = draft.email.trim().toLowerCase();
                    if (
                        newMail &&
                        newMail !== (original?.email || "").toLowerCase()
                    ) {
                        payload.email = newMail;
                    }
                }
                await api.put(`/users/${editingId}`, payload);
                toast.success("Usuario actualizado");
            } else {
                if (!draft.email.trim() || !payload.name) {
                    toast.error("Email y nombre son obligatorios");
                    return;
                }
                await api.post("/users", { ...payload, email: draft.email.trim() });
                toast.success("Usuario invitado");
            }
            cancel();
            load();
        } catch (e) {
            toast.error(extractErrorMessage(e, "Error guardando usuario"));
        }
    };

    const remove = async (u) => {
        const ok = await confirm({
            title: `¿Desactivar a ${u.name || u.email}?`,
            description:
                "El usuario no podrá volver a entrar hasta que lo reactives. La data del usuario se conserva.",
            confirmLabel: "Sí, desactivar",
            cancelLabel: "Cancelar",
            tone: "destructive",
            kind: "delete",
        });
        if (!ok) return;
        try {
            await api.delete(`/users/${u.id}`);
            toast.success("Usuario desactivado");
            load();
        } catch (e) {
            toast.error(extractErrorMessage(e, "Error"));
        }
    };

    return (
        <div className="flex min-h-screen bg-ink-950">
            <Sidebar />
            <main className="flex-1 overflow-y-auto">
                <PageTopBar />
                <div className="max-w-5xl mx-auto px-10 py-14 space-y-10">
                    <header className="flex items-start justify-between gap-6 flex-wrap">
                        <div>
                            <p className="font-mono text-[10px] tracking-[0.3em] text-fog-400 uppercase mb-2">
                                Gestión de equipo
                            </p>
                            <h1 className="font-display text-5xl text-fog-100 flex items-center gap-3">
                                <UsersIcon className="w-8 h-8 text-periwinkle-800" />
                                Usuarios
                            </h1>
                            <p className="text-fog-400 text-sm mt-2 max-w-xl">
                                {isSuper
                                    ? "Controla todos los usuarios de la plataforma, sus roles y marcas a las que acceden."
                                    : `Invita usuarios y asigna a qué marcas acceden. Tu cuota actual: ${activeCreatedCount}/${myQuota || "∞"}.`}
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <button
                                data-testid="users-refresh-btn"
                                onClick={load}
                                className="rounded-xl border hairline border-ink-600 text-fog-400 hover:text-periwinkle-800 hover:border-periwinkle-500 px-4 py-2.5 text-xs font-mono uppercase tracking-widest flex items-center gap-2"
                            >
                                <RefreshCw className="w-3.5 h-3.5" />
                                Recargar
                            </button>
                            <button
                                data-testid="users-new-btn"
                                onClick={startCreate}
                                className="rounded-xl bg-periwinkle-500 hover:bg-periwinkle-600 text-ink-on px-5 py-2.5 text-sm font-medium flex items-center gap-2"
                            >
                                <UserPlus className="w-4 h-4" />
                                Invitar usuario
                            </button>
                        </div>
                    </header>

                    {(creating || editingId) && (
                        <UserFormModal
                            draft={draft}
                            setDraft={setDraft}
                            creating={creating}
                            isSuper={isSuper}
                            brands={brands}
                            onCancel={cancel}
                            onSubmit={submit}
                        />
                    )}

                    <section className="space-y-3">
                        {/* Status filter chips · operators flip between
                            Active / Inactive / All. Counts are baked into
                            the chip so the size of each bucket is visible
                            without clicking through. Soft-deleted users
                            are NEVER counted here — the backend doesn't
                            return them. */}
                        <div
                            data-testid="users-status-filter"
                            className="flex items-center gap-2 flex-wrap"
                        >
                            {[
                                {
                                    key: "active",
                                    label: "Activos",
                                    count: activeCount,
                                    icon: Check,
                                },
                                {
                                    key: "inactive",
                                    label: "Inactivos",
                                    count: inactiveCount,
                                    icon: UserX,
                                },
                                {
                                    key: "all",
                                    label: "Todos",
                                    count: users.length,
                                    icon: UsersIcon,
                                },
                            ].map(({ key, label, count, icon: Icon }) => {
                                const active = statusFilter === key;
                                return (
                                    <button
                                        key={key}
                                        data-testid={`users-filter-${key}`}
                                        onClick={() => {
                                            setStatusFilter(key);
                                            clearSelection();
                                        }}
                                        className={`rounded-xl border hairline px-3 py-1.5 text-xs font-mono uppercase tracking-widest flex items-center gap-2 transition-colors ${
                                            active
                                                ? "bg-periwinkle-500/15 border-periwinkle-500/60 text-periwinkle-800"
                                                : "border-ink-600 text-fog-400 hover:border-periwinkle-500/40 hover:text-fog-200"
                                        }`}
                                    >
                                        <Icon className="w-3 h-3" />
                                        <span>{label}</span>
                                        <span className="text-[10px] opacity-70">
                                            {count}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>

                        {isSuper && selectedIds.size > 0 && (
                            <div
                                data-testid="users-bulk-toolbar"
                                className="rounded-xl border hairline border-periwinkle-500/60 bg-periwinkle-500/10 px-4 py-3 flex items-center gap-4 sticky top-2 z-10 flex-wrap"
                            >
                                <Check className="w-4 h-4 text-periwinkle-800 shrink-0" />
                                <p
                                    data-testid="users-bulk-count"
                                    className="text-sm text-fog-100 flex-1 min-w-[140px]"
                                >
                                    <strong>{selectedIds.size}</strong>{" "}
                                    seleccionado{selectedIds.size === 1 ? "" : "s"}
                                </p>
                                <button
                                    data-testid="users-bulk-clear-btn"
                                    onClick={clearSelection}
                                    className="text-fog-400 hover:text-fog-100 font-mono text-[10px] uppercase tracking-widest"
                                >
                                    Limpiar
                                </button>
                                <button
                                    data-testid="users-bulk-deactivate-btn"
                                    onClick={bulkDeactivate}
                                    className="rounded-xl bg-ink-700 hover:bg-ink-600 border hairline border-ink-500 text-fog-200 px-4 py-2 text-xs font-mono uppercase tracking-widest flex items-center gap-2"
                                >
                                    <UserX className="w-3.5 h-3.5" />
                                    Desactivar seleccionados
                                </button>
                                <button
                                    data-testid="users-bulk-delete-btn"
                                    onClick={bulkDelete}
                                    className="rounded-xl bg-destructive/15 hover:bg-destructive/25 border hairline border-destructive/40 text-destructive px-4 py-2 text-xs font-mono uppercase tracking-widest flex items-center gap-2"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                    Eliminar seleccionados
                                </button>
                            </div>
                        )}
                        <div className="rounded-2xl border hairline border-ink-600 bg-ink-900 overflow-hidden">
                            {loading ? (
                                <div className="p-10 text-center font-mono text-xs text-fog-400">
                                    Cargando…
                                </div>
                            ) : filteredUsers.length === 0 ? (
                                <div className="p-10 text-center font-mono text-xs text-fog-400">
                                    {users.length === 0
                                        ? "Aún no hay usuarios."
                                        : statusFilter === "active"
                                          ? "No hay usuarios activos."
                                          : statusFilter === "inactive"
                                            ? "No hay usuarios inactivos."
                                            : "Sin resultados."}
                                </div>
                            ) : (
                                <>
                                    {isSuper && selectableIds.length > 0 && (
                                        <div className="px-5 py-2.5 border-b hairline border-ink-600 bg-ink-900/60 flex items-center gap-3">
                                            <input
                                                type="checkbox"
                                                data-testid="users-select-all"
                                                checked={allSelected}
                                                onChange={toggleSelectAll}
                                                aria-label="Seleccionar todos"
                                                className="w-4 h-4 rounded border-ink-500 bg-ink-800 accent-periwinkle-500 cursor-pointer"
                                            />
                                            <span className="font-mono text-[10px] tracking-widest text-fog-400 uppercase">
                                                {allSelected
                                                    ? "Quitar selección"
                                                    : `Seleccionar todos (${selectableIds.length})`}
                                            </span>
                                        </div>
                                    )}
                                    <div className="divide-y divide-ink-600">
                                        {filteredUsers.map((u) => (
                                            <UserRow
                                                key={u.id}
                                                user={u}
                                                brands={brands}
                                                me={me}
                                                isSuper={isSuper}
                                                selectable={
                                                    isSuper &&
                                                    selectableIds.includes(u.id)
                                                }
                                                checked={selectedIds.has(u.id)}
                                                onToggleSelect={() =>
                                                    toggleSelected(u.id)
                                                }
                                                onEdit={() => startEdit(u)}
                                                onDelete={() => remove(u)}
                                                onSoftDelete={() => softDelete(u)}
                                                onImpersonate={() => impersonate(u)}
                                            />
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </section>
                </div>
            </main>
        </div>
    );
}

function blankDraft(role = "user") {
    return {
        email: "",
        name: "",
        role,
        brand_access: [],
        max_users_quota: 5,
        monthly_image_quota: 0,
        is_active: true,
    };
}

function UserRow({ user, brands, me, isSuper, selectable = false, checked = false, onToggleSelect, onEdit, onDelete, onSoftDelete, onImpersonate }) {
    const Icon = ROLE_ICONS[user.role] || UserIcon;
    const inactive = user.is_active === false;
    const allBrands = (user.brand_access || []).map(
        (id) => brands.find((b) => b.id === id)?.name || "?",
    );
    const isSelf = user.id === me?.id;
    const canEdit = me?.role === "super_admin" || user.created_by === me?.id;
    // canDeactivate covers BOTH single-user deactivate (admin + super_admin)
    // and the existing soft-delete-by-deactivation flow. Excludes self
    // and peer super_admins — the backend already rejects both, mirroring
    // the rule in the UI prevents broken affordances. canSoftDelete is
    // the proper "make the user disappear from the platform" action and
    // is SUPER_ADMIN ONLY per spec.
    const canDeactivate =
        !isSelf && canEdit && user.role !== "super_admin";
    const canSoftDelete =
        isSuper && !isSelf && user.role !== "super_admin";
    const canImpersonate =
        isSuper && !isSelf && !inactive && user.role !== "super_admin";
    return (
        <div
            data-testid={`user-row-${user.id}`}
            className={`px-5 py-4 flex items-center gap-4 ${inactive ? "opacity-50" : ""} ${checked ? "bg-periwinkle-500/5" : ""}`}
        >
            {isSuper && (
                <div className="w-4 shrink-0 flex items-center justify-center">
                    {selectable ? (
                        <input
                            type="checkbox"
                            data-testid={`user-select-${user.id}`}
                            checked={checked}
                            onChange={onToggleSelect}
                            aria-label={`Seleccionar ${user.name || user.email}`}
                            className="w-4 h-4 rounded border-ink-500 bg-ink-800 accent-periwinkle-500 cursor-pointer"
                        />
                    ) : (
                        // Keeps the column aligned for non-selectable rows
                        // (self + peer super_admins). Tooltip explains why.
                        <span
                            className="w-4 h-4 inline-block opacity-0"
                            aria-hidden="true"
                        />
                    )}
                </div>
            )}
            <div
                className="w-10 h-10 rounded-full border hairline border-ink-600 flex items-center justify-center text-sm font-mono uppercase shrink-0"
                style={{
                    background: user.avatar_color || "#a5a6ff",
                    color: "#060818",
                }}
            >
                {(user.name || user.email || "?").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-fog-100 text-sm font-medium truncate">
                        {user.name || "Sin nombre"}
                    </p>
                    <span
                        className={`font-mono text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full border hairline flex items-center gap-1 ${ROLE_STYLES[user.role] || ROLE_STYLES.user}`}
                    >
                        <Icon className="w-3 h-3" />
                        {user.role}
                    </span>
                    {inactive && (
                        <span className="font-mono text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full border hairline border-destructive/40 text-destructive">
                            Inactivo
                        </span>
                    )}
                    {isSelf && (
                        <span className="font-mono text-[9px] tracking-widest uppercase text-fog-500">
                            tú
                        </span>
                    )}
                </div>
                <p className="font-mono text-[11px] text-fog-400 truncate mt-0.5">
                    {user.email}
                </p>
                {user.role === "user" && (
                    <p className="text-[11px] text-fog-500 mt-1 truncate">
                        Marcas: {allBrands.length ? allBrands.join(", ") : "— ninguna"}
                        {(user.monthly_image_quota ?? 0) > 0 && (
                            <span className="ml-2 text-periwinkle-800">
                                · {user.monthly_image_quota} imgs/mes
                            </span>
                        )}
                    </p>
                )}
                {user.role === "admin" && (
                    <p className="text-[11px] text-fog-500 mt-1">
                        Cuota de usuarios: {user.max_users_quota ?? 5}
                    </p>
                )}
            </div>
            <div className="flex gap-2 shrink-0">
                {canImpersonate && (
                    <button
                        data-testid={`user-impersonate-${user.id}`}
                        onClick={onImpersonate}
                        className="rounded-md border hairline border-periwinkle-500/50 text-periwinkle-800 hover:text-ink-on hover:bg-periwinkle-500 px-3 py-1.5 text-xs font-mono uppercase tracking-widest flex items-center gap-1.5 transition-colors"
                        title="Ingresar como este usuario"
                    >
                        <LogIn className="w-3 h-3" />
                        Ingresar
                    </button>
                )}
                {canEdit && (
                    <button
                        data-testid={`user-edit-${user.id}`}
                        onClick={onEdit}
                        className="rounded-md border hairline border-ink-600 text-fog-400 hover:text-periwinkle-800 hover:border-periwinkle-500 px-3 py-1.5 text-xs font-mono uppercase tracking-widest"
                    >
                        Editar
                    </button>
                )}
                {canDeactivate && !inactive && (
                    <button
                        data-testid={`user-deactivate-${user.id}`}
                        onClick={onDelete}
                        className="rounded-md border hairline border-ink-600 text-fog-400 hover:text-fog-200 hover:border-fog-400 w-8 h-8 flex items-center justify-center"
                        title="Desactivar (el usuario queda visible en 'Inactivos')"
                        aria-label="Desactivar"
                    >
                        <UserX className="w-3.5 h-3.5" />
                    </button>
                )}
                {canSoftDelete && (
                    <button
                        data-testid={`user-soft-delete-${user.id}`}
                        onClick={onSoftDelete}
                        className="rounded-md border hairline border-ink-600 text-fog-400 hover:text-destructive hover:border-destructive w-8 h-8 flex items-center justify-center"
                        title="Eliminar (oculto en TODA la plataforma)"
                        aria-label="Eliminar"
                    >
                        <Trash2 className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>
        </div>
    );
}

/**
 * Wraps UserForm in a centered modal overlay so it's always visible no
 * matter where in the user list the admin clicked "Editar". Previously the
 * form was rendered inline above the list — if the list was scrolled, the
 * newly-opened form would be above the viewport and the admin perceived
 * the edit button as broken.
 */
function UserFormModal(props) {
    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && props.onCancel();
        window.addEventListener("keydown", onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [props]);
    return (
        <div
            data-testid="user-form-modal"
            className="fixed inset-0 z-[95] bg-black/40 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto animate-fade-in"
            onClick={(e) => e.target === e.currentTarget && props.onCancel()}
        >
            <div
                className="w-full max-w-2xl my-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <UserForm {...props} />
            </div>
        </div>
    );
}

function UserForm({ draft, setDraft, creating, isSuper, brands, onCancel, onSubmit }) {
    const toggleBrand = (id) => {
        setDraft((d) => ({
            ...d,
            brand_access: d.brand_access.includes(id)
                ? d.brand_access.filter((x) => x !== id)
                : [...d.brand_access, id],
        }));
    };
    return (
        <section
            data-testid="user-form"
            className="rounded-2xl border hairline border-periwinkle-500/40 bg-ink-900 p-6 space-y-5"
        >
            <div className="flex items-baseline justify-between">
                <p className="font-mono text-[10px] tracking-[0.3em] text-periwinkle-800 uppercase">
                    {creating ? "Nuevo usuario" : "Editar usuario"}
                </p>
                <button
                    data-testid="user-form-cancel"
                    onClick={onCancel}
                    className="text-fog-400 hover:text-fog-100"
                    aria-label="Cerrar"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
                <Field label={creating ? "Email" : isSuper ? "Email (editable · super admin)" : "Email"}>
                    <input
                        data-testid="user-form-email"
                        type="email"
                        value={draft.email}
                        onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                        placeholder="nombre@empresa.com"
                        // Locked in the create path is a no-op (we're
                        // building the row from scratch). During edit,
                        // only super_admin can change it — admins get a
                        // read-only field to match the backend policy.
                        disabled={!creating && !isSuper}
                        className="w-full rounded-xl bg-ink-800 border hairline border-ink-600 text-fog-100 px-4 py-2.5 outline-none focus:border-periwinkle-500 text-sm disabled:opacity-60"
                    />
                </Field>
                <Field label="Nombre">
                    <input
                        data-testid="user-form-name"
                        value={draft.name}
                        onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                        placeholder="Nombre y apellido"
                        className="w-full rounded-xl bg-ink-800 border hairline border-ink-600 text-fog-100 px-4 py-2.5 outline-none focus:border-periwinkle-500 text-sm"
                    />
                </Field>
                <Field label="Rol">
                    <select
                        data-testid="user-form-role"
                        value={draft.role}
                        onChange={(e) => setDraft({ ...draft, role: e.target.value })}
                        className="w-full rounded-xl bg-ink-800 border hairline border-ink-600 text-fog-100 px-4 py-2.5 outline-none focus:border-periwinkle-500 text-sm"
                    >
                        {ROLE_OPTIONS.filter(
                            (r) => isSuper || r.value === "user",
                        ).map((r) => (
                            <option key={r.value} value={r.value}>
                                {r.label}
                            </option>
                        ))}
                    </select>
                </Field>
                {isSuper && draft.role === "admin" && (
                    <Field label="Cuota de usuarios (solo admins)">
                        <input
                            data-testid="user-form-quota"
                            type="number"
                            min="0"
                            max="1000"
                            value={draft.max_users_quota}
                            onChange={(e) =>
                                setDraft({ ...draft, max_users_quota: e.target.value })
                            }
                            className="w-full rounded-xl bg-ink-800 border hairline border-ink-600 text-fog-100 px-4 py-2.5 outline-none focus:border-periwinkle-500 text-sm"
                        />
                    </Field>
                )}
                {draft.role === "user" && (
                    <Field label="Cuota mensual de imágenes (0 = sin límite · máx 100.000)">
                        <input
                            data-testid="user-form-image-quota"
                            type="number"
                            min="0"
                            max="100000"
                            value={draft.monthly_image_quota}
                            onChange={(e) =>
                                setDraft({
                                    ...draft,
                                    monthly_image_quota: e.target.value,
                                })
                            }
                            placeholder="Ej: 200"
                            className="w-full rounded-xl bg-ink-800 border hairline border-ink-600 text-fog-100 px-4 py-2.5 outline-none focus:border-periwinkle-500 text-sm"
                        />
                    </Field>
                )}
                {!creating && (
                    <Field label="Estado">
                        <label className="flex items-center gap-3 text-sm text-fog-100 px-4 py-2.5 rounded-xl bg-ink-800 border hairline border-ink-600">
                            <input
                                data-testid="user-form-active"
                                type="checkbox"
                                checked={!!draft.is_active}
                                onChange={(e) =>
                                    setDraft({ ...draft, is_active: e.target.checked })
                                }
                                className="accent-periwinkle-500"
                            />
                            Activo
                        </label>
                    </Field>
                )}
            </div>

            {/*
             * Permissions panel
             *
             * The shape of the access differs per role:
             *   - "user"        → sees only brands present in brand_access[]
             *   - "admin"       → sees everything; manages users they create
             *   - "super_admin" → full platform control
             *
             * Before iter 31 only the "user" case was represented and the
             * brand selector was a row of subtle pills that users couldn't
             * tell were clickable. Now we always render a dedicated panel
             * with explicit checkable cards + helper buttons + role-specific
             * explanation.
             */}
            <div
                data-testid="user-form-permissions"
                className="rounded-xl border hairline border-ink-600 bg-ink-950/50 p-4 space-y-3"
            >
                <div className="flex items-baseline justify-between">
                    <p className="font-mono text-[10px] tracking-widest text-periwinkle-800 uppercase">
                        Permisos de acceso
                    </p>
                    {draft.role === "user" && (
                        <div className="flex gap-2">
                            <button
                                type="button"
                                data-testid="user-form-brands-select-all"
                                onClick={() =>
                                    setDraft((d) => ({
                                        ...d,
                                        brand_access: brands.map((b) => b.id),
                                    }))
                                }
                                className="font-mono text-[10px] tracking-widest uppercase text-fog-400 hover:text-periwinkle-800"
                            >
                                Marcar todas
                            </button>
                            <span className="text-fog-500">·</span>
                            <button
                                type="button"
                                data-testid="user-form-brands-clear"
                                onClick={() =>
                                    setDraft((d) => ({ ...d, brand_access: [] }))
                                }
                                className="font-mono text-[10px] tracking-widest uppercase text-fog-400 hover:text-destructive"
                            >
                                Limpiar
                            </button>
                        </div>
                    )}
                </div>

                {draft.role === "super_admin" && (
                    <div className="rounded-lg bg-periwinkle-500/10 border hairline border-periwinkle-500/40 px-3 py-2.5 text-xs text-periwinkle-800">
                        <strong className="font-semibold">Super admin</strong> ·
                        acceso total a la plataforma: todas las marcas, todos los
                        usuarios, configuración global, presupuestos y storage.
                    </div>
                )}

                {draft.role === "admin" && (
                    <div className="rounded-lg bg-emerald-400/10 border hairline border-emerald-400/40 px-3 py-2.5 text-xs text-emerald-200 space-y-1">
                        <div>
                            <strong className="font-semibold">Admin</strong> · ve
                            todas las marcas automáticamente. Gestiona solo los
                            usuarios que invita (rol user).
                        </div>
                        <div className="font-mono text-[10px] text-emerald-200/80">
                            Usa la cuota de usuarios para limitar cuántos puede invitar.
                        </div>
                    </div>
                )}

                {draft.role === "user" && (
                    <>
                        <p className="text-xs text-fog-400">
                            Marca las marcas a las que este usuario puede acceder.
                            Si no seleccionas ninguna, no podrá entrar a ningún chat
                            ni biblioteca.
                        </p>
                        {brands.length === 0 ? (
                            <p className="text-xs text-fog-500 font-mono">
                                Aún no hay marcas. Crea una desde el selector del sidebar.
                            </p>
                        ) : (
                            <div className="grid sm:grid-cols-2 gap-2">
                                {brands.map((b) => {
                                    const active = draft.brand_access.includes(b.id);
                                    return (
                                        <button
                                            key={b.id}
                                            type="button"
                                            data-testid={`user-form-brand-${b.id}`}
                                            onClick={() => toggleBrand(b.id)}
                                            aria-pressed={active}
                                            className={`group w-full flex items-center gap-3 rounded-lg border hairline px-3 py-2.5 text-sm text-left transition-all ${
                                                active
                                                    ? "border-periwinkle-500 bg-periwinkle-500/10 text-fog-100"
                                                    : "border-ink-600 bg-ink-900 text-fog-300 hover:border-periwinkle-500/60 hover:bg-ink-800"
                                            }`}
                                        >
                                            <span
                                                className={`w-5 h-5 shrink-0 rounded border hairline flex items-center justify-center transition-all ${
                                                    active
                                                        ? "bg-periwinkle-500 border-periwinkle-500"
                                                        : "bg-transparent border-ink-600 group-hover:border-periwinkle-500/60"
                                                }`}
                                                aria-hidden="true"
                                            >
                                                {active && (
                                                    <Check className="w-3.5 h-3.5 text-ink-on" />
                                                )}
                                            </span>
                                            <span
                                                className="w-2.5 h-2.5 rounded-full shrink-0"
                                                style={{ background: b.color }}
                                                aria-hidden="true"
                                            />
                                            <span className="truncate">{b.name}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        <p className="font-mono text-[10px] text-fog-500">
                            {draft.brand_access.length} de {brands.length} marcas
                            seleccionadas
                        </p>
                    </>
                )}
            </div>

            <div className="flex gap-2 pt-2">
                <button
                    data-testid="user-form-submit"
                    onClick={onSubmit}
                    className="rounded-xl bg-periwinkle-500 hover:bg-periwinkle-600 text-ink-on px-5 py-2.5 text-sm font-medium flex items-center gap-2"
                >
                    <Save className="w-4 h-4" />
                    {creating ? "Invitar" : "Guardar"}
                </button>
                <button
                    onClick={onCancel}
                    className="rounded-xl border hairline border-ink-600 text-fog-400 hover:text-fog-100 px-5 py-2.5 text-sm"
                >
                    Cancelar
                </button>
            </div>
        </section>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <p className="font-mono text-[10px] tracking-widest text-fog-400 uppercase mb-2">
                {label}
            </p>
            {children}
        </div>
    );
}
